import { Button } from '@bambi/component-library/src/components/Button/Button';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { BulletList } from 'src/common/BulletList';
import { BulkTripValidateResults } from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { VerticalSortableDragAndDropList } from 'src/common/SortableDragAndDropList/SortableDragAndDropList';
import { formatFullName } from 'src/common/util/formatFullName';

import { dispatcherSlice } from '../dispatcher.slice';
import { TripCard } from '../trips/TripCard/TripCard';
import { MultiloadIcon } from './MultiloadIcon';
import { useMultiloadAssign } from './useMultiloadAssign';
import { useMultiloadOrderItems } from './useMultiloadOrderItems';

export function MultiloadOrderStep() {
  const dispatch = useAppDispatch();
  const unassignedTrips = useAppSelector(
    (state) => state.dispatcher.dispatchResponse?.unassigned_trips
  );
  const { orderedMultiLoadItems, setOrderedMultiLoadItems } =
    useMultiloadOrderItems();
  const { isLoading, serverError, validationErrors, onAssignClick } =
    useMultiloadAssign(orderedMultiLoadItems);

  if (!unassignedTrips) {
    return null;
  }

  const tripIdMetaMap = Object.fromEntries(
    orderedMultiLoadItems.map(({ trip }) => [
      trip.id,
      { passengerFullName: formatFullName(trip.passenger) },
    ])
  );

  return (
    <div className="flex min-w-[425px] max-w-md flex-col gap-4 p-2">
      {validationErrors ? (
        <FormattedBulkTripValidationError
          bulkTripValidateResults={validationErrors}
          tripIdMetaMap={tripIdMetaMap}
        />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="mt-3 flex justify-center">
            <MultiloadIcon />
          </div>
          <h2 className="-mt-2 text-center text-xl">
            Drag and drop to reorder
          </h2>
          {serverError && <FormErrorMessage>{serverError}</FormErrorMessage>}
          <div
            className={`mt-2 max-h-[50vh] overflow-auto ${
              isLoading
                ? 'disabled:cursor-not-allowed disabled:opacity-50'
                : ''
            }`}
          >
            <VerticalSortableDragAndDropList
              items={orderedMultiLoadItems}
              renderItem={(orderItem) => (
                <TripCard
                  trip={orderItem.trip}
                  hideMenu
                  variant={orderItem.type}
                />
              )}
              onReorder={setOrderedMultiLoadItems}
            />
          </div>
        </div>
      )}
      <div className="mt-4 flex gap-4">
        <Button
          className="flex-grow"
          onClick={() =>
            dispatch(dispatcherSlice.actions.onMultiloadFlowCancel())
          }
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          loading={isLoading}
          className="flex-grow"
          variant={validationErrors ? 'primary-danger' : 'primary'}
          disabled={isLoading}
          onClick={() =>
            onAssignClick({
              shouldValidate: !validationErrors,
            })
          }
        >
          {validationErrors ? 'Override and Save' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

interface BulkValidationTripMeta {
  passengerFullName: string;
}

function FormattedBulkTripValidationError({
  bulkTripValidateResults,
  tripIdMetaMap,
}: {
  bulkTripValidateResults: BulkTripValidateResults;
  tripIdMetaMap: Record<string, BulkValidationTripMeta>;
}) {
  const formattedErrors =
    bulkTripValidateResults.invalid_trips?.map((invalidTrip) => {
      const tripMeta = tripIdMetaMap[invalidTrip.trip_id];
      const explanation = `${tripMeta.passengerFullName} - ${
        invalidTrip.explanation?.join('\n ') ?? ''
      }`;
      return explanation;
    }) || [];
  return (
    <FormErrorMessage>
      {/*
        This is how we display these elsewhere, but we may want to give each its own
        FormErrorMessage instance for some separation
      */}
      <div className="pl-3">
        <BulletList items={formattedErrors} />
      </div>
    </FormErrorMessage>
  );
}
